import { gsap } from "gsap"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import { t } from "./i18n.js"

gsap.registerPlugin(ScrollToPlugin)

/**
 * Quiz controller: manages smooth scroll, screen locking, score tracking, and result display.
 */
export class Quiz {
    constructor() {
        this.screens = []
        this.currentIndex = 0
        this.score = 0
        this.totalScored = 0
        this.navArrow = null
        this.quizNid = null
        this.quizLang = null
        this.locked = false
        this.isAnimating = false
        this.subScreens = []
        this.subIndex = 0
        this.animatedTexts = []
        this.animatedIndex = 0
        this.resultTextTemplates = new WeakMap()
        this.scrollDirection = 'forward'
        this.infoBoundaryIndex = -1
        this.infosMode = false

        this.init()
    }

    init() {
        const quizEl = document.querySelector(".quiz")
        if (!quizEl) return

        this.quizEl = quizEl
        this.quizNid = quizEl.dataset.quizNid
        this.quizLang = quizEl.dataset.quizLang
        this.screens = Array.from(quizEl.querySelectorAll(".quiz__screen"))

        if (this.screens.length === 0) return

        // Count question screens for the counter
        this.totalQuestions = this.screens.filter(
            (s) => s.dataset.screenType === "question"
        ).length
        this.questionIndex = 0

        // Pre-fill all question counters on init
        let qNum = 0
        this.screens.forEach((screen) => {
            if (screen.dataset.screenType === "question") {
                qNum++
                const counters = screen.querySelectorAll("[data-question-counter]")
                counters.forEach((counter) => {
                    counter.textContent = `${qNum}/${this.totalQuestions}`
                })
                // Add large class to short titles
                const titles = screen.querySelectorAll(".question__title")
                titles.forEach((title) => {
                    if (title.textContent.trim().length < 25) {
                        title.classList.add("large")
                    }
                })
            }
        })

        this.createNavArrow()
        this.setupWheelNav()
        this.setupTouchNav()
        this.setupAnswerButtons()
        this.setupKeyboardNav()
        this.setupInfoBoundary()
        this.parseRestartButtons()

        // Navigation dots — always visible, clickable with locking rules
        const urlParams = new URLSearchParams(window.location.search)
        this.isDev = urlParams.get("dev") === "1"
        this.createNavStep()
        if (this.isDev) {
            // Dev score override — ?dev=1&score=N forces score for testing result screens
            const scoreParam = urlParams.get("score")
            if (scoreParam !== null) {
                this.score = parseInt(scoreParam, 10)
                this.totalScored = this.totalQuestions
            }
        }

        // Reset scroll position on load — browser may restore previous position
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual'
        }

        const sectionParam = urlParams.get("section")
        const startIndex = sectionParam !== null ? Math.min(parseInt(sectionParam, 10), this.screens.length - 1) : 0

        // Use gsap.set for instant positioning — window.scrollTo may be overridden by browser
        gsap.set(window, { scrollTo: { y: this.screens[startIndex] } })
        this.onScreenChange(startIndex)

        console.log("Quiz initialized (GSAP)", {
            screens: this.screens.length,
        })
    }

    // Create floating navigation arrow using the scroll-down SVG
    createNavArrow() {
        this.navArrow = document.createElement("button")
        this.navArrow.className = "quiz__nav"
        this.navArrow.setAttribute("aria-label", t("nextScreen", "Next screen"))
        this.navArrow.innerHTML = `<img src="${new URL('../images/scroll-down.svg', import.meta.url).href}" alt="" />`
        this.navArrow.addEventListener("click", () => this.next())
        this.quizEl.appendChild(this.navArrow)
    }

    // Navigation steps — always visible, locking-aware in non-dev mode
    // Groups auxiliary screens with their question: medias-pre-question (before),
    // screen_tips + answer_reveal (after) share one step with the question.
    // All screens after the first result_message also share a single step.
    createNavStep() {
        this.devNav = document.createElement("div")
        this.devNav.className = "quiz__nav--steps"
        if (this.isDev) {
            this.devNav.classList.add("is-dev")
        }
        this.visitedScreens = new Set()

        // Find the first result_message screen — everything after it is one group
        const resultIndex = this.screens.findIndex(
            (s) => s.dataset.screenType === "result_message"
        )
        const postResultStart = resultIndex !== -1 ? resultIndex + 1 : this.screens.length

        // Auxiliary types that group with a question
        const auxBefore = ["medias-pre-question"]
        const auxAfter = ["screen_tips", "answer_reveal"]

        // Collect question indices for look-ahead (medias-pre-question → next question)
        const questionIndices = []
        this.screens.forEach((s, i) => {
            if (s.dataset.screenType === "question") questionIndices.push(i)
        })

        // Pass 1: assign each screen a group key (question index, 'post-result', or self)
        const screenGroupKey = {}
        let lastQuestionIdx = -1

        this.screens.forEach((screen, index) => {
            // Post-result screens share a single group
            if (index >= postResultStart && postResultStart < this.screens.length) {
                screenGroupKey[index] = "post-result"
                return
            }
            const type = screen.dataset.screenType
            if (type === "question") {
                lastQuestionIdx = index
                screenGroupKey[index] = index
            } else if (auxBefore.includes(type)) {
                const nextQ = questionIndices.find((q) => q > index)
                screenGroupKey[index] = nextQ !== undefined ? nextQ : index
            } else if (auxAfter.includes(type)) {
                screenGroupKey[index] = lastQuestionIdx !== -1 ? lastQuestionIdx : index
            } else {
                screenGroupKey[index] = index
            }
        })

        // Pass 2: find the minimum index per group — that's the primary (gets the step button)
        const groupMinIndex = {}
        Object.entries(screenGroupKey).forEach(([idxStr, key]) => {
            const idx = parseInt(idxStr, 10)
            if (!(key in groupMinIndex) || idx < groupMinIndex[key]) {
                groupMinIndex[key] = idx
            }
        })

        // Map each screen index → primary index (the step it belongs to)
        this.screenToStepPrimary = {}
        Object.entries(screenGroupKey).forEach(([idxStr, key]) => {
            this.screenToStepPrimary[parseInt(idxStr, 10)] = groupMinIndex[key]
        })

        // Map: primary index → step element
        this.screenToStep = {}

        this.screens.forEach((screen, index) => {
            // Only create a step for primary screens (first of their group)
            if (this.screenToStepPrimary[index] !== index) return

            const step = document.createElement("button")
            step.className = "quiz__nav--step"
            step.dataset.index = index
            // Mark grouped steps (post-result or multi-screen question groups)
            const groupSize = Object.values(this.screenToStepPrimary).filter((p) => p === index).length
            if (groupSize > 1) {
                step.classList.add("quiz__nav--step--group")
            }

            const clickIndex = index
            step.addEventListener("click", () => {
                // Dev mode: bypass all locks, jump anywhere
                if (this.isDev) {
                    this.locked = false
                    this.isAnimating = false
                    gsap.killTweensOf(window)
                    this.scrollTo(clickIndex, 'forward')
                    return
                }
                const stepWasVisited = Object.entries(this.screenToStepPrimary).some(
                    ([screenIndex, primaryIndex]) =>
                        primaryIndex === clickIndex && this.visitedScreens.has(parseInt(screenIndex, 10))
                )
                // Reopen the informational boundary when its previously visited navigation step is clicked.
                const infoPrimaryIndex = this.screenToStepPrimary[this.infoBoundaryIndex]
                if (clickIndex === infoPrimaryIndex && this.visitedScreens.has(this.infoBoundaryIndex)) {
                    this.enterInfosMode()
                    return
                }
                // Navigation dots may leave informational mode only for previously visited steps.
                if (this.infosMode) {
                    if (!stepWasVisited) return
                    this.exitInfosMode()
                }
                // Non-dev: respect question locking — can't jump past unanswered questions
                if (!this.canNavigateTo(clickIndex)) return
                this.isAnimating = false
                gsap.killTweensOf(window)
                this.scrollTo(clickIndex, 'forward')
            })

            // Map all screens sharing this primary to this step
            this.screenToStep[index] = step
            this.devNav.appendChild(step)
        })

        this.quizEl.appendChild(this.devNav)

        // Update active and visited steps on screen change
        this.updateDevNav = (index) => {
            this.visitedScreens.add(index)
            const activeStep = this.screenToStep[this.screenToStepPrimary[index]]
            this.devNav.querySelectorAll(".quiz__nav--step").forEach((step) => {
                step.classList.toggle("is-active", step === activeStep)
                // Mark as visited if any screen mapped to this step has been visited
                const isVisited = Object.entries(this.screenToStepPrimary).some(
                    ([si, primary]) =>
                        this.screenToStep[primary] === step &&
                        this.visitedScreens.has(parseInt(si, 10)) &&
                        this.screenToStep[primary] !== activeStep
                )
                step.classList.toggle("is-visited", isVisited)
            })
        }
    }

    // Check if navigation to a target index is allowed (non-dev locking rules)
    canNavigateTo(targetIndex) {
        // Can always go backward to visited screens
        if (targetIndex <= this.currentIndex) return true
        // For forward navigation, check no unanswered question blocks the path
        for (let i = this.currentIndex; i < targetIndex; i++) {
            const screen = this.screens[i]
            if (screen.dataset.screenType === "question" && screen.dataset.answered === "false") {
                return false
            }
        }
        return true
    }

    // Enter a targeted section or sub-screen and prevent navigation above its parent screen.
    enterInfosMode(sectionId = null) {
        let targetSubScreen = null
        if (sectionId) {
            const target = document.getElementById(sectionId)
            const targetScreen = target?.closest('.quiz__screen')
            const boundaryIndex = this.screens.indexOf(targetScreen)
            if (boundaryIndex === -1) return false
            this.infoBoundaryIndex = boundaryIndex
            targetSubScreen = target.matches('.screens') ? target : null
        }
        if (this.infoBoundaryIndex === -1) return false

        this.infosMode = true
        this.unlock()
        this.isAnimating = false
        gsap.killTweensOf(window)
        this.scrollDirection = 'forward'
        this.onScreenChange(this.infoBoundaryIndex)
        if (targetSubScreen) {
            const subIndex = this.subScreens.indexOf(targetSubScreen)
            if (subIndex !== -1) {
                this.showSubScreen(subIndex, 'forward', true)
                this.updateNavArrowNested()
            }
        }
        gsap.set(window, { scrollTo: { y: this.screens[this.infoBoundaryIndex], autoKill: false } })
        return true
    }

    // Leave the informational section so explicit menu navigation can return to the quiz.
    exitInfosMode() {
        this.infosMode = false
    }

    // Keep native or programmatic upward scrolling from crossing the informational boundary.
    setupInfoBoundary() {
        window.addEventListener("scroll", () => {
            if (!this.infosMode || this.infoBoundaryIndex === -1 || this.isAnimating) return

            const boundaryTop = this.screens[this.infoBoundaryIndex].offsetTop
            if (window.scrollY < boundaryTop) {
                gsap.set(window, { scrollTo: { y: boundaryTop, autoKill: false } })
            }
        }, { passive: true })
    }

    // Check if backward navigation is allowed while an unanswered question keeps the quiz locked
    canScrollBack() {
        if (!this.locked) return true
        if (this.canNavigateCarousel('backward')) return true
        if (this.subScreens.length > 0 && this.subIndex > 0) return true

        const current = this.screens[this.currentIndex]
        const prev = this.screens[this.currentIndex - 1]
        return current.dataset.screenType === "question" &&
            this.lockedQuestionIndex === this.currentIndex &&
            prev?.dataset.screenType === "medias-pre-question"
    }

    // Check if forward navigation is allowed while an unanswered question keeps the quiz locked
    canScrollForward() {
        if (!this.locked) return true
        if (this.canNavigateCarousel('forward')) return true
        if (this.subScreens.length > 0 && this.subIndex < this.subScreens.length - 1) return true

        const current = this.screens[this.currentIndex]
        const next = this.screens[this.currentIndex + 1]
        return current.dataset.screenType === "medias-pre-question" &&
            this.lockedQuestionIndex === this.currentIndex + 1 &&
            next?.dataset.screenType === "question" &&
            next.dataset.answered === "false"
    }

    // Intercept wheel events — one gesture = one screen navigation
    setupWheelNav() {
        let wheelLock = false
        document.addEventListener("wheel", (e) => {
            e.preventDefault()
            if (this.isAnimating || wheelLock) return
            if (Math.abs(e.deltaY) < 20) return
            // When locked, only allow navigation within the question's media flow
            if (this.locked) {
                if (e.deltaY > 0 && !this.canScrollForward()) return
                if (e.deltaY < 0 && !this.canScrollBack()) return
            }
            wheelLock = true
            if (e.deltaY > 0) this.next()
            else if (e.deltaY < 0) this.prev()
            setTimeout(() => { wheelLock = false }, 800)
        }, { passive: false })
    }

    // Intercept touch events — vertical swipe = screen navigation, horizontal = carousel
    setupTouchNav() {
        let touchStartY = 0
        let touchStartX = 0
        let isHorizontal = false
        let isVerticalCarousel = false
        let verticalCarouselEl = null
        let isPinching = false
        let wasPinching = false

        document.addEventListener("touchstart", (e) => {
            // Fresh single-finger touch clears pinch cooldown
            if (e.touches.length === 1) {
                wasPinching = false
            }
            touchStartY = e.touches[0].clientY
            touchStartX = e.touches[0].clientX
            isHorizontal = false
            isPinching = e.touches.length > 1
            if (isPinching) wasPinching = true
            // Check if touch started inside a vertical carousel
            verticalCarouselEl = e.target.closest(".carousel--vertical")
            isVerticalCarousel = verticalCarouselEl !== null
        }, { passive: true })

        // Check if vertical carousel is at boundary and swipe direction would overflow
        const isCarouselAtBoundary = (deltaY) => {
            if (!verticalCarouselEl) return false
            const swiper = verticalCarouselEl.swiper
            if (!swiper) return false
            // Swipe up (deltaY < 0) at last slide → allow quiz nav forward
            if (deltaY < 0 && swiper.isEnd) return true
            // Swipe down (deltaY > 0) at first slide → allow quiz nav backward
            if (deltaY > 0 && swiper.isBeginning) return true
            return false
        }

        document.addEventListener("touchmove", (e) => {
            if (this.isAnimating) { e.preventDefault(); return }
            // Let pinch-to-zoom gestures pass through (including residual move after finger lift)
            if (isPinching || wasPinching || e.touches.length > 1) return
            // Let vertical carousel handle its own swipes unless at boundary
            if (isVerticalCarousel) {
                const deltaY = e.touches[0].clientY - touchStartY
                if (!isCarouselAtBoundary(deltaY)) return
            }
            const deltaY = Math.abs(e.touches[0].clientY - touchStartY)
            const deltaX = Math.abs(e.touches[0].clientX - touchStartX)
            // If horizontal movement dominates, let carousel handle it
            if (deltaX > deltaY && deltaX > 10) {
                isHorizontal = true
            }
            // Block vertical native scroll
            if (!isHorizontal) {
                e.preventDefault()
            }
        }, { passive: false })

        document.addEventListener("touchend", (e) => {
            if (this.isAnimating || isHorizontal || isPinching || wasPinching) return
            const deltaY = e.changedTouches[0].clientY - touchStartY
            // If inside vertical carousel, only allow quiz nav at boundaries
            if (isVerticalCarousel && !isCarouselAtBoundary(deltaY)) return
            if (Math.abs(deltaY) < 50) return
            if (this.locked) {
                if (deltaY < 0 && !this.canScrollForward()) return
                if (deltaY > 0 && !this.canScrollBack()) return
            }
            if (deltaY < 0) this.next()
            else if (deltaY > 0) this.prev()
        }, { passive: true })
    }

    // Called when active screen changes
    onScreenChange(index) {
        this.currentIndex = index
        const screen = this.screens[index]
        const type = screen.dataset.screenType

        // Update dev nav active dot
        if (this.updateDevNav) this.updateDevNav(index)

        // Check if we need to lock scroll
        if (type === "question" && screen.dataset.answered === "false") {
            this.lock()
        } else if (type === "medias-pre-question" && this.lockedQuestionIndex === index + 1) {
            // Stay locked when on the medias-pre-question paired with a locked question
        } else {
            this.unlock()
        }

        // Prepare the score-specific result content before detecting nested animated text
        if (type === "result_message") {
            this.showResult(screen)
        }

        // Detect sub-screens (.screens elements) on any screen for internal scroll navigation
        this.subScreens = Array.from(screen.querySelectorAll(".screens"))
        if (this.subScreens.length > 0) {
            // When going backward, start at last sub-screen; forward starts at first
            this.subIndex = this.scrollDirection === 'backward' ? this.subScreens.length - 1 : 0
            this.showSubScreen(this.subIndex, this.scrollDirection, true)
        } else {
            this.subIndex = 0
            this.resetCarousels(screen, this.scrollDirection)
        }

        // Detect animated text reveal scoped to the active sub-screen (or the screen itself if no sub-screens)
        const animatedContainer = this.subScreens.length > 0
            ? this.subScreens[this.subIndex]
            : screen
        this.animatedTexts = Array.from(animatedContainer.querySelectorAll(".animated.content .text:not([hidden])"))
        if (this.animatedTexts.length > 0) {
            // Forward: start with first text visible; backward: start with all texts visible
            this.animatedIndex = this.scrollDirection === 'backward' ? this.animatedTexts.length - 1 : 0
            this.showAnimatedTexts(this.animatedIndex, true)
        } else {
            this.animatedIndex = 0
        }

        // Show the arrow through question media steps, then hide it until the answer is selected
        if (type === "question" && screen.dataset.answered === "false") {
            const hasMoreSubs = this.subScreens.length > 0 && this.subIndex < this.subScreens.length - 1
            this.navArrow.classList.toggle("is-hidden", !hasMoreSubs)
        } else if (index < this.screens.length - 1) {
            this.navArrow.classList.remove("is-hidden")
        } else if (type === "result_message") {
            // Show nav arrow if there's more nested content to reveal
            const hasMoreSubs = this.subScreens.length > 0 && this.subIndex < this.subScreens.length - 1
            const hasMoreTexts = this.animatedTexts.length > 0 && this.animatedIndex < this.animatedTexts.length - 1
            if (hasMoreSubs || hasMoreTexts) {
                this.navArrow.classList.remove("is-hidden")
            } else {
                this.navArrow.classList.add("is-hidden")
            }
        } else {
            this.navArrow.classList.add("is-hidden")
        }

        // Dispatch custom event for other modules
        screen.dispatchEvent(new CustomEvent("quiz:screen-change", {
            detail: { index, screen, type },
            bubbles: true,
        }))
    }

    // Lock scrolling — user must answer the question
    lock() {
        this.locked = true
        this.lockedQuestionIndex = this.currentIndex
    }

    // Unlock scrolling
    unlock() {
        this.locked = false
        this.lockedQuestionIndex = null
    }

    // Return the Swiper instance visible in the active screen or sub-screen.
    getActiveCarousel() {
        const screen = this.screens[this.currentIndex]
        const container = this.subScreens.length > 0 ? this.subScreens[this.subIndex] : screen
        return container?.querySelector('[data-carousel]')?.swiper ?? null
    }

    // Check whether the active carousel has another slide in the requested direction.
    canNavigateCarousel(direction) {
        const swiper = this.getActiveCarousel()
        if (!swiper || swiper.slides.length <= 1) return false
        return direction === 'forward' ? !swiper.isEnd : !swiper.isBeginning
    }

    // Move the active carousel before allowing navigation to continue through the quiz.
    navigateCarousel(direction) {
        const swiper = this.getActiveCarousel()
        if (!swiper || swiper.slides.length <= 1) return false
        if (swiper.animating) return true
        if (direction === 'forward' && !swiper.isEnd) {
            swiper.slideNext()
            return true
        }
        if (direction === 'backward' && !swiper.isBeginning) {
            swiper.slidePrev()
            return true
        }
        return false
    }

    // Reset carousels when their containing screen becomes active.
    resetCarousels(container, direction) {
        container.querySelectorAll('[data-carousel]').forEach((carousel) => {
            const swiper = carousel.swiper
            if (!swiper) return
            swiper.slideTo(direction === 'backward' ? swiper.slides.length - 1 : 0, 0)
        })
    }

    // Scroll to next screen (or next carousel slide / sub-screen / animated text if inside)
    next() {
        if (this.isAnimating || (this.locked && !this.canScrollForward())) return
        // Advance the active carousel before leaving its current content step
        if (this.navigateCarousel('forward')) {
            this.updateNavArrowNested()
            return
        }
        // If inside sub-screens, advance sub-screen first
        if (this.subScreens.length > 0 && this.subIndex < this.subScreens.length - 1) {
            this.showSubScreen(this.subIndex + 1, 'forward')
            this.updateNavArrowNested()
            return
        }
        // If inside animated text reveal, reveal next text first
        if (this.animatedTexts.length > 0 && this.animatedIndex < this.animatedTexts.length - 1) {
            this.animatedIndex++
            this.revealAnimatedText(this.animatedIndex)
            this.updateNavArrowNested()
            return
        }
        if (this.currentIndex < this.screens.length - 1) {
            this.scrollTo(this.currentIndex + 1)
        }
    }

    // Scroll to previous screen (or previous carousel slide / animated text / sub-screen if inside)
    prev() {
        if (this.isAnimating) return
        // Rewind the active carousel before leaving its current content step
        if (this.navigateCarousel('backward')) {
            this.updateNavArrowNested()
            return
        }
        // If inside animated text reveal, hide last text first (deepest level)
        if (this.animatedTexts.length > 0 && this.animatedIndex > 0) {
            this.hideAnimatedText(this.animatedIndex)
            this.animatedIndex--
            this.updateNavArrowNested()
            return
        }
        // If inside sub-screens, go back sub-screen first
        if (this.subScreens.length > 0 && this.subIndex > 0) {
            this.showSubScreen(this.subIndex - 1, 'backward')
            this.updateNavArrowNested()
            return
        }
        if (this.currentIndex > 0) {
            this.scrollTo(this.currentIndex - 1)
        }
    }

    // Update nav arrow visibility based on nested content remaining in current screen
    updateNavArrowNested() {
        if (!this.navArrow) return
        const screen = this.screens[this.currentIndex]
        const hasMoreSubs = this.subScreens.length > 0 && this.subIndex < this.subScreens.length - 1
        const hasMoreTexts = this.animatedTexts.length > 0 && this.animatedIndex < this.animatedTexts.length - 1
        const hasMoreCarouselSlides = this.canNavigateCarousel('forward')
        const waitsForAnswer = screen.dataset.screenType === "question" && screen.dataset.answered === "false"
        if (hasMoreSubs || hasMoreTexts || hasMoreCarouselSlides) {
            this.navArrow.classList.remove("is-hidden")
        } else if (waitsForAnswer || this.currentIndex >= this.screens.length - 1) {
            this.navArrow.classList.add("is-hidden")
        }
    }

    // Show a specific sub-screen with translate Y animation (up on forward, down on backward)
    showSubScreen(index, direction = 'forward', instant = false) {
        if (instant) gsap.killTweensOf(this.subScreens)

        this.subScreens.forEach((s, i) => {
            if (i === index) {
                s.classList.add('active')
                if (instant) {
                    gsap.set(s, { y: '0%', opacity: 1, scale: 1 })
                } else {
                    gsap.fromTo(s,
                        { y: direction === 'forward' ? '50%' : '-50%', opacity: 0, scale: 0.6 },
                        { y: '0%', opacity: 1, scale: 1, duration: 0.5, ease: 'expo.out' }
                    )
                }
            } else if (instant) {
                gsap.set(s, { y: '0%', opacity: 0, scale: 1 })
                s.classList.remove('active')
            } else if (s.classList.contains('active')) {
                gsap.to(s,
                    { y: direction === 'forward' ? '-50%' : '50%', opacity: 0, scale: 0.6, duration: 0.5, ease: 'expo.out',
                      onComplete: () => { s.classList.remove('active') }
                    }
                )
            }
        })
        this.subIndex = index
        this.resetCarousels(this.subScreens[index], direction)

        const activeSub = this.subScreens[index]
        const screen = this.screens[this.currentIndex]
        screen.dispatchEvent(new CustomEvent("quiz:sub-screen-change", {
            detail: { screen, subScreen: activeSub, index },
            bubbles: true,
        }))

        // Detect animated texts inside the newly active sub-screen
        this.animatedTexts = activeSub ? Array.from(activeSub.querySelectorAll(".animated.content .text:not([hidden])")) : []
        if (this.animatedTexts.length > 0) {
            this.animatedIndex = direction === 'backward' ? this.animatedTexts.length - 1 : 0
            this.showAnimatedTexts(this.animatedIndex, true)
        } else {
            this.animatedIndex = 0
        }
    }

    // Set initial visibility state for animated texts (instant, no animation)
    showAnimatedTexts(visibleCount, instant = false) {
        this.animatedTexts.forEach((el, i) => {
            if (i <= visibleCount) {
                el.classList.add('active')
                if (instant) gsap.set(el, { clearProps: 'all' })
            } else {
                el.classList.remove('active')
                if (instant) gsap.set(el, { clearProps: 'all' })
            }
        })
    }

    // Reveal a specific animated text — FLIP: container transitions while text slides in
    revealAnimatedText(index) {
        const el = this.animatedTexts[index]
        if (!el) return
        const container = el.parentElement

        // Capture container position before layout change
        const beforeRect = container.getBoundingClientRect()

        // Add text to layout (display:block) but invisible
        el.classList.add('active')
        gsap.set(el, { y: '30%', opacity: 0 })

        // Capture container position after layout change
        const afterRect = container.getBoundingClientRect()
        const delta = beforeRect.top - afterRect.top

        // FLIP: start container at old position, animate to new while text slides in
        gsap.set(container, { y: delta })
        gsap.to(container, { y: 0, duration: 0.5, ease: 'expo.out', clearProps: 'all' })
        gsap.to(el, { y: '0%', opacity: 1, duration: 0.5, ease: 'expo.out', clearProps: 'all' })
    }

    // Hide a specific animated text — FLIP: text removed from flow, container re-centers while text slides out
    hideAnimatedText(index) {
        const el = this.animatedTexts[index]
        if (!el) return
        const container = el.parentElement

        // Capture container position before layout change
        const beforeRect = container.getBoundingClientRect()

        // Get text position relative to container for absolute positioning
        const elRect = el.getBoundingClientRect()
        const elTop = elRect.top - beforeRect.top
        const elLeft = elRect.left - beforeRect.left
        const elWidth = elRect.width

        // Remove text from flow with absolute positioning at same spot
        gsap.set(el, { position: 'absolute', top: elTop, left: elLeft, width: elWidth })

        // Capture container position after text removed from flow
        const afterRect = container.getBoundingClientRect()
        const delta = beforeRect.top - afterRect.top

        // FLIP: animate container to new position while text fades/slides out
        gsap.set(container, { y: delta })
        gsap.to(container, { y: 0, duration: 0.4, ease: 'expo.out', clearProps: 'all' })
        gsap.to(el, {
            y: '30%', opacity: 0, duration: 0.4, ease: 'expo.out',
            onComplete: () => {
                el.classList.remove('active')
                gsap.set(el, { clearProps: 'all' })
            }
        })
    }

    // Scroll to a specific screen index — fullpage-style animated navigation
    scrollTo(index, forceDirection = null) {
        if (index < 0 || index >= this.screens.length) return
        if (this.infosMode && index < this.infoBoundaryIndex) return
        if (this.isAnimating) return
        const target = this.screens[index]
        this.isAnimating = true
        gsap.killTweensOf(window)
        this.scrollDirection = forceDirection || (index > this.currentIndex ? 'forward' : 'backward')
        this.currentIndex = index
        // Call onScreenChange before animation so content (e.g. result) is ready when scroll arrives
        this.onScreenChange(index)
        gsap.to(window, {
            scrollTo: { y: target, autoKill: false },
            duration: 0.8,
            ease: "expo.out",
            onComplete: () => {
                this.isAnimating = false
            },
        })
    }

    // Setup answer button click handlers
    setupAnswerButtons() {
        document.addEventListener("click", (e) => {
            const btn = e.target.closest("[data-answer]")
            if (!btn) return
            this.handleAnswer(btn)
        })
    }

    // Handle answer button click
    handleAnswer(btn) {
        const screen = btn.closest(".quiz__screen")
        if (!screen || screen.dataset.answered === "true") return

        const userAnswer = btn.dataset.answer
        const correctAnswer = screen.dataset.correctAnswer
        const countsInScore = screen.dataset.countsInScore === "1"

        // Mark as answered
        screen.dataset.answered = "true"

        // Highlight buttons
        const buttons = screen.querySelectorAll("[data-answer]")
        buttons.forEach((b) => {
            b.classList.add("is-disabled")
            if (b.dataset.answer === correctAnswer) {
                b.classList.add("is-correct")
            } else if (b.dataset.answer === userAnswer && userAnswer !== correctAnswer) {
                b.classList.add("is-wrong")
            }
        })

        // Track score
        if (countsInScore) {
            this.totalScored++
            if (userAnswer === correctAnswer) {
                this.score++
            }
            this.updateScoreDisplay()
        }
        this.updateRestartLabels()

        // Show feedback message based on answer correctness
        const feedbackType = userAnswer === correctAnswer ? "correct" : "wrong"
        const feedback = screen.querySelector(`[data-feedback="${feedbackType}"]`)
        if (feedback) {
            feedback.hidden = false
            gsap.from(feedback, {
                opacity: 0,
                y: 20,
                duration: 0.5,
                ease: "power2.out",
            })
        }

        // Unlock scroll — user navigates manually via nav arrow
        this.unlock()
        this.navArrow.classList.remove("is-hidden")
    }

    // Update score display (score shown inline on result screen, no fixed display)
    updateScoreDisplay() {
        // No-op — fixed score display removed, score shown on result screen via #score div
    }

    // Show the appropriate result message based on score — populate #score div, parse dynamic tokens
    showResult(screen) {
        const resultMessages = screen.querySelectorAll(".result-message")

        resultMessages.forEach((msg) => {
            const min = parseInt(msg.dataset.scoreMin, 10) || 0
            const max = parseInt(msg.dataset.scoreMax, 10) || Infinity

            if (this.score >= min && this.score <= max) {
                msg.style.display = "flex"
                msg.querySelectorAll("[data-message-score-min]").forEach((message) => {
                    const messageMin = parseInt(message.dataset.messageScoreMin, 10)
                    const messageMax = message.dataset.messageScoreMax === ""
                        ? Infinity
                        : parseInt(message.dataset.messageScoreMax, 10)
                    message.hidden = this.score < messageMin || this.score > messageMax
                    message.classList.remove("active")
                })
                // Populate the inline score div with the calculated score
                const scoreEl = msg.querySelector("#score")
                if (scoreEl) {
                    scoreEl.textContent = `${this.score}/${this.totalScored}`
                }
                // Parse dynamic tokens in all text nodes within the result message
                this.parseResultTokens(msg)
            } else {
                msg.style.display = "none"
            }
        })

        this.submitScore()
    }

    // Replace dynamic tokens from their original templates on every result display.
    parseResultTokens(container) {
        const isPlural = this.score !== 1
        const walker = document.createTreeWalker(
            container,
            NodeFilter.SHOW_TEXT,
            null
        )
        const textNodes = []
        let node
        while ((node = walker.nextNode())) {
            if (!this.resultTextTemplates.has(node) && node.nodeValue.includes("[")) {
                this.resultTextTemplates.set(node, node.nodeValue)
            }
            if (this.resultTextTemplates.has(node)) {
                textNodes.push(node)
            }
        }
        textNodes.forEach((textNode) => {
            let text = this.resultTextTemplates.get(textNode)
            // Replace score and total from the current attempt.
            text = text.replace(/\[result\]/g, String(this.score))
            text = text.replace(/\[total\]/g, String(this.totalScored))
            // Replace singular/plural forms from the current score.
            text = text.replace(/\[(\S+?)_(\S+)\]/g, (_, singular, plural) => {
                return isPlural ? plural : singular
            })
            textNode.nodeValue = text
        })
    }

    // Scan all screens for [RESTART_BUTTON] tokens and replace them with a working restart button
    parseRestartButtons() {
        const token = "[RESTART_BUTTON]"
        this.screens.forEach((screen) => {
            const walker = document.createTreeWalker(
                screen,
                NodeFilter.SHOW_TEXT,
                null
            )
            const textNodes = []
            let node
            while ((node = walker.nextNode())) {
                if (node.nodeValue.includes(token)) {
                    textNodes.push(node)
                }
            }
            textNodes.forEach((textNode) => {
                // If the token fills the whole parent element, replace the parent entirely
                const parent = textNode.parentElement
                if (parent && parent.textContent.trim() === token) {
                    const btn = this.createRestartButton()
                    parent.replaceWith(btn)
                } else {
                    // Otherwise replace just the text node with the button
                    const btn = this.createRestartButton()
                    textNode.replaceWith(btn)
                }
            })
        })
    }

    // Create the restart button element wired to the restart handler
    createRestartButton() {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = "question__btn button button--primary quiz__restart"
        btn.textContent = this.getRestartLabel()
        btn.addEventListener("click", () => this.restart())
        return btn
    }

    // Check whether the visitor has answered at least one quiz question.
    hasStartedQuiz() {
        return this.totalScored > 0 || this.screens.some(
            (screen) => screen.dataset.screenType === "question" && screen.dataset.answered === "true"
        )
    }

    // Return the localized start or restart label based on quiz progress.
    getRestartLabel() {
        return this.hasStartedQuiz()
            ? t("restartTest", "Restart the test")
            : t("startTest", "Start the test")
    }

    // Refresh every generated start/restart button after quiz progress changes.
    updateRestartLabels() {
        this.quizEl.querySelectorAll(".quiz__restart").forEach((button) => {
            button.textContent = this.getRestartLabel()
        })
    }

    // Restart the quiz — reset progress and return to the first question.
    restart() {
        // Reset score tracking
        this.score = 0
        this.totalScored = 0
        this.questionIndex = 0

        // Reset every screen's answered state and clear answer button highlights
        this.screens.forEach((screen) => {
            if (screen.dataset.screenType === "question") {
                screen.dataset.answered = "false"
            }
            screen.querySelectorAll("[data-answer]").forEach((b) => {
                b.classList.remove("is-disabled", "is-correct", "is-wrong")
            })
            // Hide all answer feedback messages
            screen.querySelectorAll("[data-feedback]").forEach((fb) => {
                fb.hidden = true
            })
            // Hide all result messages — showResult will reveal the matching one
            screen.querySelectorAll(".result-message").forEach((msg) => {
                msg.style.display = "none"
            })
        })

        // Reset nav step visited/active states
        if (this.visitedScreens) this.visitedScreens.clear()
        if (this.devNav) {
            this.devNav.querySelectorAll(".quiz__nav--step").forEach((step) => {
                step.classList.remove("is-visited", "is-active")
            })
        }

        // Leave informational mode and start again from the first question.
        this.exitInfosMode()
        this.unlock()
        this.isAnimating = false
        this.updateRestartLabels()
        gsap.killTweensOf(window)
        const firstQuestionIndex = this.screens.findIndex(
            (screen) => screen.dataset.screenType === "question"
        )
        this.scrollTo(firstQuestionIndex === -1 ? 0 : firstQuestionIndex, "forward")
    }

    // Submit score anonymously via AJAX
    async submitScore() {
        if (!this.quizNid || this.totalScored === 0) return

        try {
            await fetch("/api/quiz-score", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    quiz_nid: parseInt(this.quizNid, 10),
                    score: this.score,
                    total: this.totalScored,
                    langcode: this.quizLang || "fr",
                }),
            })
        } catch (e) {
            console.warn("Score submission failed:", e)
        }
    }

    // Keyboard navigation (ArrowDown/ArrowUp/PageDown/PageUp/Space)
    setupKeyboardNav() {
        const scrollKeys = ["ArrowDown", "ArrowUp", "PageDown", "PageUp", " ", "Space"]
        document.addEventListener("keydown", (e) => {
            if (!scrollKeys.includes(e.key)) return
            e.preventDefault()
            if (this.isAnimating) return

            const isForward = e.key === "ArrowDown" || e.key === "PageDown" || e.key === " "
            const isBackward = e.key === "ArrowUp" || e.key === "PageUp"

            // When locked, constrain navigation to the unanswered question's allowed steps
            if (this.locked) {
                if (isForward && !this.canScrollForward()) return
                if (isBackward && !this.canScrollBack()) return
            }

            if (isForward) {
                this.next()
            } else if (isBackward) {
                this.prev()
            }
        })
    }
}
